import mermaid from "mermaid";

import { tr } from "../i18n/i18n.svelte";

let initialized = false;
function ensureInit(): void {
  if (initialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: "default",
    securityLevel: "strict",
    fontSize: 11,
  });
  initialized = true;
}

let renderCounter = 0;
const svgCache = new Map<string, string>();

export function renderMermaidBlocks(container: HTMLElement): void {
  // Synchronously inject from cache (no flicker when {@html html} replaces the DOM mid-stream).
  applyCachedRenders(container);
  void renderUncachedBlocks(container);
}

function applyCachedRenders(container: HTMLElement): void {
  for (const { pre, source } of findMermaidCandidates(container)) {
    const cached = svgCache.get(source);
    if (cached) replacePreWithMermaid(pre, source, cached);
  }
}

const errorCache = new Set<string>();

async function renderUncachedBlocks(container: HTMLElement): Promise<void> {
  ensureInit();
  for (const { pre, source } of findMermaidCandidates(container)) {
    const cached = svgCache.get(source);
    if (cached) {
      replacePreWithMermaid(pre, source, cached);
      continue;
    }

    // Skip blocks that are likely still streaming: marked auto-closes an unfinished
    // ``` block with the rest of the message stuffed inside, but if anything follows
    // the <pre>, the closing fence has been parsed → safe to render.
    if (!isLikelyComplete(pre)) continue;

    if (errorCache.has(source)) {
      ensureErrorBanner(pre, errorCache.has(source) ? tr({ fr: "(voir console)", en: "(see console)" }) : "");
      continue;
    }

    const id = `mermaid-svg-${++renderCounter}`;
    const cleaned = sanitizeMermaidSource(source);
    try {
      const { svg } = await mermaid.render(id, cleaned);
      svgCache.set(source, svg);
      const fresh = findPreWithSource(container, source);
      if (fresh) replacePreWithMermaid(fresh, source, svg);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[mermaid] render failed", err, { source, cleaned });
      errorCache.add(source);
      const fresh = findPreWithSource(container, source);
      if (fresh) ensureErrorBanner(fresh, message);
    }
  }
}

/** Diagramme hors écran → PNG, pour les fichiers générés (PDF, Word). Null si
 *  la source ne se rend pas : l'appelant garde alors le code. */
export async function renderMermaidToPng(
  source: string,
): Promise<{ blob: Blob; width: number; height: number } | null> {
  ensureInit();
  try {
    const { svg } = await mermaid.render(`mermaid-file-${++renderCounter}`, sanitizeMermaidSource(source));
    const holder = document.createElement("div");
    holder.innerHTML = svg;
    const svgEl = holder.querySelector<SVGSVGElement>("svg");
    return svgEl ? await renderSvgToPngBlob(svgEl) : null;
  } catch (err) {
    console.warn("[mermaid] file render failed", err);
    return null;
  }
}

/** Le bloc de code est-il un diagramme Mermaid (```mermaid ou ```flowchart…) ? */
export function isMermaidLang(lang: string): boolean {
  const l = lang.trim().toLowerCase();
  return l === "mermaid" || MERMAID_KEYWORDS.some((k) => k.toLowerCase() === l);
}

/**
 * Best-effort fix-ups for common LLM mistakes that crash the Mermaid parser:
 *  - Internal pipes inside an edge label (`-->|foo | bar| B`) — replaced by em-dash and quoted.
 *  - Unquoted edge labels containing parentheses, slashes, colons, commas — auto-quoted.
 *  - Unquoted node labels containing special chars (`B[CHUV & HUG]`) — auto-quoted to `B["CHUV & HUG"]`.
 * Applied line-by-line; lines that don't contain an arrow are passed through unchanged
 * for edge fixes, but node fixes apply to every line (declarations can stand alone).
 */
function sanitizeMermaidSource(source: string): string {
  return source
    .split("\n")
    .map(sanitizeEdgeLabel)
    .map(sanitizeNodeLabels)
    .join("\n");
}

const ARROW_RE = /(-->|---|==>|===|-\.->|-\.-|\.->|\.\.>)/;
const EDGE_SPECIAL_CHAR_RE = /[(),:/;]/;
const NODE_SPECIAL_CHAR_RE = /[&(),:/;]/;
// Matches a Mermaid node declaration: identifier followed by a square-bracket label.
// Identifier = letters/digits/underscore (no spaces). Label = anything except square brackets.
const NODE_RE = /(\b[A-Za-z][A-Za-z0-9_]*)\[([^\]\[]*)\]/g;

function sanitizeEdgeLabel(line: string): string {
  const m = ARROW_RE.exec(line);
  if (!m) return line;
  const headEnd = m.index + m[0].length;
  const after = line.slice(headEnd);
  const firstPipe = after.indexOf("|");
  if (firstPipe === -1) return line;
  const afterFirstPipe = after.slice(firstPipe + 1);
  const lastPipe = afterFirstPipe.lastIndexOf("|");
  if (lastPipe === -1) return line;

  const labelContent = afterFirstPipe.slice(0, lastPipe);
  const target = afterFirstPipe.slice(lastPipe + 1);
  const trimmed = labelContent.trim();

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return line;

  const hasInternalPipe = labelContent.includes("|");
  const hasSpecialChar = EDGE_SPECIAL_CHAR_RE.test(labelContent);
  if (!hasInternalPipe && !hasSpecialChar) return line;

  let cleanedLabel = hasInternalPipe
    ? labelContent
        .split(/\s*\|\s*/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .join(" — ")
    : labelContent.trim();
  cleanedLabel = cleanedLabel.replace(/"/g, "");

  return (
    line.slice(0, headEnd) +
    after.slice(0, firstPipe) +
    `|"${cleanedLabel}"|` +
    target
  );
}

function sanitizeNodeLabels(line: string): string {
  return line.replace(NODE_RE, (match, id: string, label: string) => {
    const trimmed = label.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) return match;
    if (!NODE_SPECIAL_CHAR_RE.test(label)) return match;
    const cleaned = label.replace(/"/g, "");
    return `${id}["${cleaned}"]`;
  });
}

function ensureErrorBanner(pre: HTMLElement, message: string): void {
  const prev = pre.previousElementSibling;
  if (prev && prev.classList.contains("mermaid-error")) return;
  const banner = document.createElement("div");
  banner.className = "mermaid-error";
  banner.textContent = `${tr({ fr: "Rendu Mermaid échoué", en: "Mermaid rendering failed" })} — ${message.slice(0, 400)}`;
  pre.parentNode?.insertBefore(banner, pre);
}

const MERMAID_KEYWORDS = [
  "flowchart", "graph", "sequenceDiagram", "classDiagram",
  "stateDiagram", "stateDiagram-v2", "erDiagram", "journey",
  "gantt", "pie", "gitGraph", "mindmap", "timeline",
  "quadrantChart", "requirementDiagram", "C4Context", "xychart-beta",
  "block-beta", "sankey-beta", "kanban", "architecture-beta",
];

function findMermaidCandidates(
  container: HTMLElement,
): Array<{ pre: HTMLElement; source: string }> {
  const out: Array<{ pre: HTMLElement; source: string }> = [];
  const codeEls = Array.from(
    container.querySelectorAll<HTMLElement>("pre > code"),
  );
  for (const codeEl of codeEls) {
    const pre = codeEl.parentElement;
    if (!pre || pre.tagName !== "PRE") continue;
    const source = (codeEl.textContent ?? "").trim();
    if (!source) continue;
    if (!isMermaidCandidate(codeEl, source)) continue;
    out.push({ pre, source });
  }
  return out;
}

function isMermaidCandidate(codeEl: HTMLElement, source: string): boolean {
  if (codeEl.classList.contains("language-mermaid")) return true;

  // The LLM may tag the fence with the diagram type itself (```flowchart, ```sequenceDiagram…)
  // instead of ```mermaid. Accept those, but skip any other tagged language (json, ts, js…).
  const mermaidKeywordsLower = MERMAID_KEYWORDS.map((k) => k.toLowerCase());
  for (const c of codeEl.classList) {
    if (!c.startsWith("language-")) continue;
    const lang = c.slice("language-".length).toLowerCase();
    if (mermaidKeywordsLower.includes(lang)) return true;
    return false;
  }

  // No language tag: sniff the first non-empty line for a Mermaid keyword.
  const firstLine = source.split("\n", 1)[0]?.trim() ?? "";
  return mermaidKeywordsLower.some((k) => firstLine.toLowerCase().startsWith(k));
}

function isLikelyComplete(pre: HTMLElement): boolean {
  let node: Node | null = pre.nextSibling;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) return true;
    if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim()) return true;
    node = node.nextSibling;
  }
  return false;
}

function findPreWithSource(container: HTMLElement, source: string): HTMLElement | null {
  for (const candidate of findMermaidCandidates(container)) {
    if (candidate.source === source) return candidate.pre;
  }
  return null;
}

function replacePreWithMermaid(pre: HTMLElement, source: string, svg: string): void {
  const block = document.createElement("div");
  block.className = "mermaid-block";
  block.dataset.mermaidState = "rendered";

  const figure = document.createElement("div");
  figure.className = "mermaid-figure";
  figure.innerHTML = svg;

  const toolbar = document.createElement("div");
  toolbar.className = "mermaid-toolbar";

  const exportBtn = document.createElement("button");
  exportBtn.type = "button";
  exportBtn.className = "mermaid-export";
  exportBtn.textContent = tr({ fr: "Télécharger PNG", en: "Download PNG" });
  exportBtn.addEventListener("click", () => {
    const svgEl = figure.querySelector<SVGSVGElement>("svg");
    if (svgEl) downloadAsPng(svgEl, `${tr({ fr: "flux-donnees", en: "data-flow" })}-${Date.now()}.png`);
  });

  const copyBtn = document.createElement("button");
  copyBtn.type = "button";
  copyBtn.className = "mermaid-copy";
  const copyLabel = tr({ fr: "Copier source", en: "Copy source" });
  copyBtn.textContent = copyLabel;
  copyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(source);
      copyBtn.textContent = tr({ fr: "Copié ✓", en: "Copied ✓" });
      setTimeout(() => (copyBtn.textContent = copyLabel), 1500);
    } catch {
      /* clipboard unavailable */
    }
  });

  toolbar.appendChild(exportBtn);
  toolbar.appendChild(copyBtn);
  block.appendChild(figure);
  block.appendChild(toolbar);

  pre.replaceWith(block);
  attachEdgeCitations(block, figure);
}

/**
 * Make each Mermaid edge label clickable by parsing the "**Sources par flèche**"
 * legend that the LLM places right after the diagram. For every legend entry
 * `- Source → Cible (Label) [N: "..."]`, we find the matching edge label in the
 * SVG (by exact label text) and tag it with `class="citation" data-refs="N"` so
 * the existing onCitationClick handler in MessageBubble picks it up via event
 * bubbling — no new click logic needed.
 */
function attachEdgeCitations(block: HTMLElement, figure: HTMLElement): void {
  const legend = findCitationLegend(block);
  if (!legend) return;
  const entries = parseLegendEntries(legend);
  if (entries.length === 0) return;

  const edgeLabels = Array.from(
    figure.querySelectorAll<HTMLElement>("span.edgeLabel"),
  );
  const used = new Set<HTMLElement>();
  for (const { label, refs, quote } of entries) {
    const span = pickEdgeLabel(edgeLabels, label, used);
    if (!span) continue;
    used.add(span);
    span.classList.add("citation");
    span.setAttribute("data-refs", refs.join(","));
    if (quote) span.setAttribute("data-quote", quote);
    span.style.cursor = "pointer";
    span.title = `${tr({ fr: "Cliquer pour ouvrir la source", en: "Click to open the source" })} [${refs.join(", ")}]`;
  }
}

function findCitationLegend(block: HTMLElement): HTMLElement | null {
  let node: Element | null = block.nextElementSibling;
  // Step over heading-like paragraphs ("Sources par flèche :"), real headings,
  // or empty separators until we hit the actual UL/OL.
  let hops = 0;
  while (node && hops < 4) {
    if (node.tagName === "UL" || node.tagName === "OL") return node as HTMLElement;
    const text = (node.textContent ?? "").trim().toLowerCase();
    if (
      /^h[1-6]$/i.test(node.tagName) ||
      (node.tagName === "P" && (text.startsWith("sources par") || text === ""))
    ) {
      node = node.nextElementSibling;
      hops += 1;
      continue;
    }
    return null;
  }
  return null;
}

function parseLegendEntries(
  legend: HTMLElement,
): Array<{ label: string; refs: number[]; quote: string | null }> {
  const out: Array<{ label: string; refs: number[]; quote: string | null }> = [];
  const items = Array.from(legend.querySelectorAll<HTMLLIElement>(":scope > li"));
  for (const li of items) {
    const text = (li.textContent ?? "").trim();
    const m = /\(([^()]+)\)/.exec(text);
    if (!m) continue;
    const label = m[1].trim();

    const spans = li.querySelectorAll<HTMLElement>("span.citation[data-refs]");
    const refs: number[] = [];
    let quote: string | null = null;
    for (const s of spans) {
      const parsed = (s.dataset.refs ?? "")
        .split(",")
        .map((x) => parseInt(x, 10))
        .filter((n) => !Number.isNaN(n));
      refs.push(...parsed);
      if (!quote && s.dataset.quote) quote = s.dataset.quote;
    }
    if (refs.length > 0) out.push({ label, refs, quote });
  }
  return out;
}

function pickEdgeLabel(
  candidates: HTMLElement[],
  labelText: string,
  used: Set<HTMLElement>,
): HTMLElement | null {
  const target = normalizeLabel(labelText);
  if (!target) return null;
  for (const c of candidates) {
    if (used.has(c)) continue;
    if (normalizeLabel(c.textContent ?? "") === target) return c;
  }
  return null;
}

function normalizeLabel(s: string): string {
  return s
    .replace(/ /g, " ")
    .replace(/\s+/g, " ")
    .replace(/^["']+|["']+$/g, "")
    .trim()
    .toLowerCase();
}

function downloadAsPng(svg: SVGSVGElement, filename: string): void {
  void renderSvgToPngBlob(svg).then((result) => {
    if (!result) return;
    const pngUrl = URL.createObjectURL(result.blob);
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(pngUrl), 1000);
  });
}

/**
 * Rasterize a Mermaid SVG to a PNG blob (white background, 2× scaling for
 * crispness). Returns the canvas pixel size so callers can preserve aspect
 * ratio when embedding (e.g. in an `<img>` for the Word export).
 *
 * Exported because exportConversation reuses this — the alternative would be
 * to inline SVG, but Word's SVG support is patchy across versions and PNG
 * just works everywhere (including SharePoint preview, which is the point).
 */
export async function renderSvgToPngBlob(
  svg: SVGSVGElement,
): Promise<{ blob: Blob; width: number; height: number } | null> {
  const { width, height } = resolveSvgSize(svg);

  // Clone with explicit width/height — the live SVG has only a viewBox,
  // which leaves <img> with zero natural size and a blank canvas.
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  if (!clone.getAttribute("xmlns")) {
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }

  const xml = new XMLSerializer().serializeToString(clone);
  const dataUrl =
    "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);

  const scale = 2;
  const canvasW = Math.max(1, Math.round(width * scale));
  const canvasH = Math.max(1, Math.round(height * scale));

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.drawImage(img, 0, 0, canvasW, canvasH);
      canvas.toBlob((blob) => {
        if (!blob) resolve(null);
        else resolve({ blob, width, height });
      }, "image/png");
    };
    img.onerror = (err) => {
      console.warn("[mermaid] SVG → PNG failed", err);
      resolve(null);
    };
    img.src = dataUrl;
  });
}

function resolveSvgSize(svg: SVGSVGElement): { width: number; height: number } {
  const rect = svg.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return { width: rect.width, height: rect.height };
  }
  const vb = svg.viewBox?.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) {
    return { width: vb.width, height: vb.height };
  }
  const w = parseFloat(svg.getAttribute("width") ?? "");
  const h = parseFloat(svg.getAttribute("height") ?? "");
  if (w > 0 && h > 0) return { width: w, height: h };
  return { width: 800, height: 600 };
}
